import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Copy, Pencil, Check, ClipboardList, Paperclip, Trash2 } from "lucide-react";
import { toast } from "sonner";

const blankTopic = () => ({
  title: "", category: "General", short_description: "", overview: "",
  discussion_points: [], hazards: [], suggested_controls: [], questions: [],
  required_ppe: [], estimated_duration_minutes: 10,
});

const lines = (arr) => (arr || []).join("\n");
const toArr = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

export function TopicDialog({ open, onOpenChange, topic, categories, isOffice, onSaved }) {
  // mode: "view" | "edit" | "create"
  const [mode, setMode] = useState("view");
  const [form, setForm] = useState(blankTopic());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (topic) { setMode("view"); setForm({ ...blankTopic(), ...topic }); }
    else { setMode("create"); setForm(blankTopic()); }
  }, [open, topic]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const editable = topic && !topic.is_system_template;

  const [atts, setAtts] = useState([]);
  const [uploadingAtt, setUploadingAtt] = useState(false);
  useEffect(() => { setAtts(topic?.attachments || []); }, [topic]);
  const attHref = (a) => `${API}/files/view?path=${encodeURIComponent(a.path)}&download=1&auth=${encodeURIComponent(localStorage.getItem("fl_token") || "")}`;
  const uploadAtt = async (file) => {
    if (!file) return;
    setUploadingAtt(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/tbx/topics/${topic.id}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAtts((a) => [...a, data]);
      toast.success("Attachment added");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploadingAtt(false); }
  };
  const removeAtt = async (aid) => {
    try { await api.delete(`/tbx/topics/${topic.id}/attachment/${aid}`); setAtts((a) => a.filter((x) => x.id !== aid)); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't remove"); }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Give the topic a title");
    setSaving(true);
    try {
      const body = {
        title: form.title, category: form.category, short_description: form.short_description,
        overview: form.overview, discussion_points: form.discussion_points,
        hazards: form.hazards, suggested_controls: form.suggested_controls,
        questions: form.questions, required_ppe: form.required_ppe,
        estimated_duration_minutes: Number(form.estimated_duration_minutes) || 10,
      };
      if (mode === "edit") await api.patch(`/tbx/topics/${topic.id}`, body);
      else await api.post("/tbx/topics", body);
      toast.success(mode === "edit" ? "Topic updated" : "Topic created");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async () => {
    try {
      await api.post(`/tbx/topics/${topic.id}/duplicate`);
      toast.success("Copied to your library — customise it under your topics");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Duplicate failed");
    }
  };

  const isForm = mode === "edit" || mode === "create";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-[#0D141C] border-zinc-200 dark:border-white/12" data-testid="topic-dialog">
        <DialogHeader>
          <DialogTitle className="uppercase tracking-tight font-display text-2xl flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-[#FF5F15]" />
            {mode === "create" ? "New topic" : mode === "edit" ? "Edit topic" : form.title}
          </DialogTitle>
          <DialogDescription>
            {isForm ? "Build a reusable safety topic for your crews." : form.short_description}
          </DialogDescription>
        </DialogHeader>

        {!isForm && (
          <div className="space-y-4" data-testid="topic-view">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="uppercase text-[10px] tracking-wider">{form.category}</Badge>
              {topic?.is_system_template
                ? <span className="text-[10px] uppercase tracking-wider text-zinc-400">Fieldlink template</span>
                : <span className="text-[10px] uppercase tracking-wider text-[#FF5F15]">Your topic</span>}
              <span className="text-[10px] uppercase tracking-wider text-zinc-400 ml-auto">~{form.estimated_duration_minutes} min</span>
            </div>
            {form.overview && <p className="text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">{form.overview}</p>}
            {(form.discussion_points || []).length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF5F15] font-bold mb-1.5">Discussion points</div>
                <ol className="space-y-1 list-decimal list-inside text-sm text-zinc-700 dark:text-zinc-200">
                  {form.discussion_points.map((p, i) => <li key={i}>{p}</li>)}
                </ol>
              </div>
            )}
            {(form.required_ppe || []).length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF5F15] font-bold mb-1.5">Required PPE</div>
                <div className="flex flex-wrap gap-1.5">
                  {form.required_ppe.map((p, i) => <Badge key={i} variant="secondary">{p}</Badge>)}
                </div>
              </div>
            )}
            {(atts.length > 0 || (isOffice && editable)) && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF5F15] font-bold mb-1.5">Attachments</div>
                <div className="space-y-1.5">
                  {atts.map((a) => (
                    <div key={a.id} className="flex items-center gap-2 text-sm border border-zinc-200 dark:border-white/10 rounded-md px-2.5 py-1.5" data-testid={`topic-att-${a.id}`}>
                      <Paperclip className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                      <a href={attHref(a)} target="_blank" rel="noreferrer" className="truncate flex-1 text-zinc-800 dark:text-zinc-200 hover:text-[#FF5F15]" data-testid={`topic-att-link-${a.id}`}>{a.name}</a>
                      {isOffice && editable && (
                        <button onClick={() => removeAtt(a.id)} className="text-zinc-400 hover:text-red-600 shrink-0" data-testid={`topic-att-del-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  ))}
                  {atts.length === 0 && <p className="text-xs text-zinc-500">No attachments yet.</p>}
                </div>
                {isOffice && editable && (
                  <label className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 border-2 border-dashed border-zinc-300 dark:border-white/15 hover:border-[#FF5F15] hover:text-[#FF5F15] text-[11px] font-bold uppercase tracking-wider cursor-pointer rounded-md" data-testid="topic-att-upload">
                    <Paperclip className="w-3.5 h-3.5" /> {uploadingAtt ? "Uploading…" : "Attach PDF / file"}
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.txt" className="hidden" disabled={uploadingAtt} onChange={(e) => { uploadAtt(e.target.files?.[0]); e.target.value = ""; }} />
                  </label>
                )}
                {isOffice && !editable && atts.length === 0 && (
                  <p className="text-[11px] text-zinc-400 mt-1">Duplicate this template to attach your own files.</p>
                )}
              </div>
            )}
          </div>
        )}

        {isForm && (
          <div className="space-y-3">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-bold">Title</Label>
                <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="topic-title" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Category</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="topic-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Short description</Label>
              <Input value={form.short_description} onChange={(e) => set("short_description", e.target.value)} data-testid="topic-desc" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Overview / purpose</Label>
              <Textarea rows={2} value={form.overview} onChange={(e) => set("overview", e.target.value)} data-testid="topic-overview" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Discussion points (one per line)</Label>
              <Textarea rows={5} value={lines(form.discussion_points)} onChange={(e) => set("discussion_points", toArr(e.target.value))} className="font-mono text-sm" data-testid="topic-points" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Required PPE (one per line)</Label>
                <Textarea rows={3} value={lines(form.required_ppe)} onChange={(e) => set("required_ppe", toArr(e.target.value))} className="font-mono text-sm" data-testid="topic-ppe" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Duration (minutes)</Label>
                <Input type="number" value={form.estimated_duration_minutes} onChange={(e) => set("estimated_duration_minutes", e.target.value)} data-testid="topic-duration" />
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {!isForm && isOffice && (
            editable
              ? <Button onClick={() => setMode("edit")} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider" data-testid="topic-edit-btn"><Pencil className="w-4 h-4 mr-1.5" /> Edit</Button>
              : <Button onClick={duplicate} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider" data-testid="topic-duplicate-btn"><Copy className="w-4 h-4 mr-1.5" /> Duplicate & customise</Button>
          )}
          {isForm && (
            <Button onClick={save} disabled={saving} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider" data-testid="topic-save-btn">
              <Check className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save topic"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
